import type { ReactNode, SyntheticEvent } from 'react';
import { useCallback, useContext, useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports
import type { FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';

import { logger } from '@luna/logger';

import type { CustomFieldErrors } from './FormValidationError';
import { get, mapErrorsToDotNotation, setErrors } from './ErrorsService';
import type { SetErrors } from './type';
import type { LunaFormContextValue } from './LunaFormContext';
import { LunaFormContext } from './LunaFormContext';
import { scrollAndFocusFirstError } from './utils/scrollAndFocusFirstError';

interface Props {
    id?: string;
    dataCy?: string;
    className?: string;
    autoComplete?: 'on' | 'off';
    children: ReactNode;
}

export const Form = <FormValue extends Record<string, any>>({
    autoComplete,
    children,
    className,
    dataCy,
    id,
}: Props) => {
    const context = useContext(LunaFormContext);

    const { onSubmit, form, autoScrollToError, autoFocusOnError } =
        context as LunaFormContextValue<FormValue>;

    const handleErrors: SetErrors<FormValue> = useCallback(
        (errors) => {
            if (!form) {
                return;
            }

            setErrors(form, errors);
        },
        [form],
    );

    const handleSubmit = useCallback(
        (values: FormValue) => {
            if (!form || !onSubmit) {
                return Promise.resolve();
            }

            try {
                return onSubmit(values, { setErrors: handleErrors, reset: form.reset });
            } catch (e: unknown) {
                logger.error('Unexpected submit error - missing custom error handling', e);
                return Promise.reject(e);
            }
        },
        [form, onSubmit, handleErrors],
    );

    /**
     * Take each invalid field and mark it as touched
     */
    const handleError = useCallback(
        (errors: unknown) => {
            if (!form) {
                return;
            }

            const values = form.getValues();
            const typedErrors = errors as CustomFieldErrors;
            const dotNotationErrors = mapErrorsToDotNotation<FormValue>(typedErrors);
            logger.debug('Submit error', typedErrors);

            Object.keys(dotNotationErrors).forEach((errorKey) => {
                form.setValue(
                    errorKey as FieldPath<FormValue>,
                    get(values, errorKey) as FieldPathValue<FieldValues, FieldPath<FormValue>>,
                    {
                        shouldTouch: true,
                    },
                );
            });

            scrollAndFocusFirstError(form, {
                autoScrollToError,
                autoFocusOnError,
            });
        },
        [form, autoScrollToError, autoFocusOnError],
    );
    const handleFormSubmit = useCallback(
        (event: SyntheticEvent) => {
            if (!form) {
                return;
            }

            // https://github.com/react-hook-form/react-hook-form/discussions/3704
            // we need to stop this event because it would trigger forms inside nested FormProviders as well
            event.stopPropagation();

            form.handleSubmit(handleSubmit, handleError)(event);
        },
        [form, handleError, handleSubmit],
    );

    useEffect(() => {
        if (!form) {
            logger.error('<Form /> must be used as a child of <LunaForm />');
        }
    }, [form]);

    return (
        <form
            id={id}
            onSubmit={handleFormSubmit}
            noValidate
            data-cy={dataCy}
            className={className}
            autoComplete={autoComplete}
        >
            {autoComplete === 'off' && (
                // https://gist.github.com/niksumeiko/360164708c3b326bd1c8#disable-html-form-input-autocomplete-and-autofill
                <input autoComplete="false" name="hidden" type="text" className="d-none" />
            )}
            {children}
        </form>
    );
};
